import { FormType, GetAssetByIdResponseDTO } from '@gearbelt/global';

export type ProcessedForm = NonNullable<GetAssetByIdResponseDTO['asset']['forms']>[number] & { isPrimary?: boolean };
export type ProcessedSchedule = NonNullable<GetAssetByIdResponseDTO['asset']['schedules']>[number] & { isPrimary?: boolean };

/**
 * Gets unique forms of a specific type from an asset's forms array
 */
export function getUniqueFormsByType(forms: GetAssetByIdResponseDTO['asset']['forms'] | undefined, formType: FormType): ProcessedForm[] {
  const relevantForms = forms?.filter((form) => form.type === formType) || [];
  if (!relevantForms.length) return [];

  const uniqueFormsMap = relevantForms.reduce((map, form) => {
    if (!map.has(form.id)) {
      map.set(form.id, { ...form, isPrimary: false });
    }
    return map;
  }, new Map<string, ProcessedForm>());

  return Array.from(uniqueFormsMap.values());
}

/**
 * Gets unique schedules from an asset's schedules array
 */
export function getUniqueSchedulesByType(schedules: GetAssetByIdResponseDTO['asset']['schedules'] | undefined): ProcessedSchedule[] {
  const uniqueSchedulesMap = (schedules || []).reduce((map, schedule) => {
    if (!map.has(schedule.id)) {
      map.set(schedule.id, { ...schedule, isPrimary: false });
    }
    return map;
  }, new Map<string, ProcessedSchedule>());

  return Array.from(uniqueSchedulesMap.values());
}

/**
 * Processes forms to determine which ones are primary based on the latest deployment rule
 * For inspection forms, all forms are considered primary
 * For repair and schedule forms, only the form with the latest deployment rule is primary
 */
export function getProcessedForms(
  forms: GetAssetByIdResponseDTO['asset']['forms'] | undefined,
  formType: FormType.RepairForm | FormType.ScheduleForm,
): ProcessedForm[] {
  const relevantOriginalForms = forms?.filter((form) => form.type === formType) || [];
  if (!relevantOriginalForms.length) return [];

  // Find the latest rule among all relevant forms (including duplicates)
  let latestRuleDate = 0;
  let latestRuleFormId: string | null = null;
  let latestRuleId: string | null = null;

  for (const form of relevantOriginalForms) {
    const ruleDate = form.deploymentRule?.createdAt ? new Date(form.deploymentRule.createdAt).getTime() : 0;
    if (form.deploymentRule && ruleDate >= latestRuleDate) {
      latestRuleDate = ruleDate;
      latestRuleFormId = form.id;
      latestRuleId = form.deploymentRule.id;
    }
  }

  // Get unique forms first
  const uniqueForms = getUniqueFormsByType(forms, formType);

  // Add isPrimary flag to the unique forms
  const processedForms = uniqueForms.map((form) => ({
    ...form,
    isPrimary: form.id === latestRuleFormId && !!relevantOriginalForms.find((f) => f.id === form.id && f.deploymentRule?.id === latestRuleId),
  }));

  // Sort unique forms with the primary one first
  return processedForms.sort((a, b) => {
    if (a.isPrimary === b.isPrimary) return 0; // Keep original order if both primary/not primary
    return a.isPrimary ? -1 : 1;
  });
}

/**
 * Processes schedules to determine which ones are primary based on the plan limit.
 * Schedules are expected to be pre-sorted by backend (deploymentRule.createdAt ASC, position ASC).
 * First N schedules (based on planLimit) are marked as primary/active.
 * @param schedules - The schedules from the asset
 * @param planLimit - Maximum number of active schedules allowed (default: 1 for backwards compatibility)
 */
export function getProcessedSchedules(schedules: GetAssetByIdResponseDTO['asset']['schedules'] | undefined, planLimit: number = 1): ProcessedSchedule[] {
  if (!schedules?.length) return [];

  // Get unique schedules (preserving order from backend)
  const uniqueSchedules = getUniqueSchedulesByType(schedules);

  // Mark first N schedules as primary based on planLimit
  return uniqueSchedules.map((schedule, index) => ({
    ...schedule,
    isPrimary: index < planLimit,
  }));
}

/**
 * Returns a consistent warning message for schedule plan limits.
 * @param limit - The schedule limit for the plan (defaults to 1)
 * @param planType - The plan type name (defaults to 'Standard')
 */
export function getScheduleLimitWarning(limit: number | undefined, planType: string | undefined): string {
  const effectiveLimit = limit ?? 1;
  const effectivePlanType = planType || 'Standard';
  return `Maximum of ${effectiveLimit} schedule${effectiveLimit > 1 ? 's' : ''} per asset allowed on your ${effectivePlanType} plan.`;
}
